import { useEffect, useRef, useState } from 'react'
import { formatRate } from '../bookingDetails/bookingDetailsData'
import { useSuggestProducts, suggestionPrice } from '../bookingDetails/useSuggestProducts'
import { CategoryOptions } from '../bookingDetails/SuggestProductPanel'

/**
 * In-call "Suggest Product" — the video console's compact, dark-themed take
 * on the appointment page's SuggestProductPanel. Same engine
 * (useSuggestProducts), squeezed into the narrow sidebar: stacked filters,
 * single-column result rows, and a horizontal strip of suggestion tiles for
 * this booking. Renders inside the console's HostSideCard shell.
 *
 * Runs the engine in deferred mode: a suggestion is a cart row, so sending
 * each pick as it is made would rewrite the customer's cart while they are
 * still on the call, watching. Picks are staged per booking (localStorage, via
 * useSuggestProducts) and nothing is published from here — ending the call
 * writes nothing. The batch goes out when the astrologer hits Confirm Finalize
 * on the appointment page, which is also where the staged picks resurface.
 */

const fieldCls =
  'w-full rounded-xl border border-[#C8922A]/20 bg-[#14100D] px-3 py-2 text-xs text-white/85 placeholder:text-white/25 focus:outline-none focus:border-[#C8922A] disabled:opacity-50'
const fieldLabelCls = 'block text-[9px] font-bold uppercase tracking-[0.16em] text-white/35 mb-1'

export default function SuggestProductCard({ booking, disabled }) {
  const [showFilters, setShowFilters] = useState(false)
  const {
    filters, setField, clearFilters, activeFilterCount, submitSearch,
    searchState, results, fetching, searchError, page, setPage,
    categories, grades,
    suggestions, suggestionsLoading, suggestedProductIds,
    suggest, addingId, removeSuggestion, removingId, error,
    pendingCount,
  } = useSuggestProducts(booking, { perPage: 5, deferred: true })

  return (
    <div className="space-y-3">
      {/* Suggestions pinned so far — a one-tile-tall strip that scrolls
          sideways, so a long list can't stretch the sidebar. */}
      {suggestionsLoading ? (
        <div className="space-y-2 animate-pulse">
          <div className="h-12 rounded-xl bg-white/5" />
        </div>
      ) : suggestions.length ? (
        <SuggestionsBox
          suggestions={suggestions}
          disabled={disabled}
          removingId={removingId}
          onRemove={removeSuggestion}
        />
      ) : (
        <p className="text-[10px] text-white/35 text-center rounded-xl border border-dashed border-white/10 px-3 py-3.5">
          🪔 No remedies suggested yet — search the catalog below.
        </p>
      )}

      {/* The customer sees none of this until the note is finalized — say so,
          or the astrologer has no way to tell staged picks from published ones. */}
      {pendingCount > 0 && (
        <p className="rounded-xl border border-[#C8922A]/25 bg-[#C8922A]/10 px-3 py-2 text-[10px] text-[#E6C878]/90 leading-relaxed">
          {`${pendingCount} change${pendingCount === 1 ? '' : 's'} pending — shared with the customer when you finalize this consultation on the appointment page.`}
        </p>
      )}

      {error && (
        <p className="rounded-xl border border-red-400/30 bg-red-500/10 px-3 py-2 text-[10px] text-red-300">{error}</p>
      )}

      {disabled ? (
        <p className="text-[10px] text-white/35">🔒 Suggestions are locked for this appointment.</p>
      ) : (
        <>
          {/* Search */}
          <form onSubmit={submitSearch} className="space-y-2 pt-1 border-t border-white/5">
            <input
              type="text"
              value={filters.q}
              onChange={setField('q')}
              placeholder="Search — e.g. Yellow Sapphire…"
              className={`${fieldCls} mt-2`}
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowFilters((v) => !v)}
                className={`inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-[9px] font-bold uppercase tracking-[0.16em] transition-colors ${
                  showFilters || activeFilterCount > 0
                    ? 'border-[#C8922A]/50 bg-[#C8922A]/15 text-[#E6C878]'
                    : 'border-white/15 text-white/45 hover:border-[#C8922A]/40 hover:text-[#E6C878]'
                }`}
              >
                ⚙ Refine
                {activeFilterCount > 0 && (
                  <span className="inline-flex items-center justify-center min-w-4 h-4 px-1 rounded-full bg-[#C8922A] text-[#14100D] text-[8px] font-bold">
                    {activeFilterCount}
                  </span>
                )}
              </button>
              <button
                type="submit"
                disabled={fetching}
                className="grow inline-flex items-center justify-center gap-1.5 rounded-xl px-4 py-2 text-[9px] font-bold uppercase tracking-[0.16em] bg-linear-to-r from-[#C8922A] to-[#E5A820] text-[#14100D] hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {fetching ? 'Searching…' : '🔍 Search'}
              </button>
            </div>
          </form>

          {/* Stacked filters */}
          {showFilters && (
            <div className="rounded-xl border border-white/10 bg-white/3 p-3 space-y-2.5">
              <div>
                <label className={fieldLabelCls}>Category</label>
                <select value={filters.category_id} onChange={setField('category_id')} className={fieldCls}>
                  <option value="">All categories</option>
                  <CategoryOptions categories={categories} />
                </select>
              </div>
              <div>
                <label className={fieldLabelCls}>Quality Grade</label>
                <select value={filters.product_grade_id} onChange={setField('product_grade_id')} className={fieldCls}>
                  <option value="">Any grade</option>
                  {grades.map((g) => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                <div>
                  <label className={fieldLabelCls}>Ratti</label>
                  <input type="number" min="0" step="0.01" value={filters.ratti} onChange={setField('ratti')} placeholder="7.5" className={fieldCls} />
                </div>
                <div>
                  <label className={fieldLabelCls}>Carat</label>
                  <input type="number" min="0" step="0.01" value={filters.carat} onChange={setField('carat')} placeholder="6.75" className={fieldCls} />
                </div>
                <div>
                  <label className={fieldLabelCls}>Min Price</label>
                  <input type="number" min="0" value={filters.min_price} onChange={setField('min_price')} placeholder="₹500" className={fieldCls} />
                </div>
                <div>
                  <label className={fieldLabelCls}>Max Price</label>
                  <input type="number" min="0" value={filters.max_price} onChange={setField('max_price')} placeholder="₹50000" className={fieldCls} />
                </div>
              </div>
              <div className="flex items-center justify-between pt-1">
                <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                  <span className="relative inline-block w-8 h-4.5">
                    <input type="checkbox" checked={filters.in_stock} onChange={setField('in_stock')} className="peer sr-only" />
                    <span className="absolute inset-0 rounded-full bg-white/15 peer-checked:bg-[#C8922A] transition-colors" />
                    <span className="absolute top-0.5 left-0.5 w-3.5 h-3.5 rounded-full bg-white shadow transition-transform peer-checked:translate-x-3.5" />
                  </span>
                  <span className="text-[10px] text-white/70">In-stock only</span>
                </label>
                <button
                  type="button"
                  onClick={clearFilters}
                  className="text-[9px] font-bold uppercase tracking-widest text-white/35 hover:text-[#E6C878] transition-colors"
                >
                  ✕ Clear
                </button>
              </div>
            </div>
          )}

          {/* Results */}
          {searchState === 'loading' && (
            <div className="space-y-2 animate-pulse">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-14 rounded-xl bg-white/5" />
              ))}
            </div>
          )}
          {searchState === 'error' && (
            <p className="rounded-xl border border-red-400/30 bg-red-500/10 px-3 py-2.5 text-[10px] text-red-300">{searchError}</p>
          )}
          {searchState === 'ready' && !results.items.length && (
            <p className="text-[10px] text-white/35 text-center rounded-xl border border-dashed border-white/10 px-3 py-4">
              ✨ No products matched — try a broader search.
            </p>
          )}
          {searchState === 'ready' && results.items.length > 0 && (
            <div className={fetching ? 'opacity-60 pointer-events-none' : ''}>
              <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-white/30 mb-2">
                {results.total} product{results.total === 1 ? '' : 's'} found
              </p>
              <ul className="space-y-2">
                {results.items.map((product) => (
                  <ResultRow
                    key={product.id}
                    product={product}
                    suggested={suggestedProductIds.has(Number(product.id))}
                    adding={addingId === product.id}
                    onSuggest={suggest}
                  />
                ))}
              </ul>
              {results.lastPage > 1 && (
                <div className="flex items-center justify-between mt-2.5">
                  <PagerBtn label="← Prev" disabled={page <= 1} onClick={() => setPage(page - 1)} />
                  <span className="text-[9px] uppercase tracking-widest text-white/30">
                    {results.currentPage} / {results.lastPage}
                  </span>
                  <PagerBtn label="Next →" disabled={page >= results.lastPage} onClick={() => setPage(page + 1)} />
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

/**
 * Suggested products as a horizontal strip of box-shaped tiles — the strip is
 * one tile tall and scrolls sideways (wheel/touch plus ◀▶ buttons), so the
 * sidebar card never grows with the list.
 */
function SuggestionsBox({ suggestions, disabled, removingId, onRemove }) {
  const scrollRef = useRef(null)
  const [canLeft, setCanLeft] = useState(false)
  const [canRight, setCanRight] = useState(false)

  const syncScroll = () => {
    const el = scrollRef.current
    if (!el) return
    setCanLeft(el.scrollLeft > 2)
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2)
  }

  // Re-measure when tiles are added/removed (a removal can kill the overflow).
  useEffect(() => {
    syncScroll()
  }, [suggestions.length])

  const nudge = (dir) => {
    scrollRef.current?.scrollBy({ left: dir * 240, behavior: 'smooth' })
  }

  const overflowing = canLeft || canRight

  return (
    <div className="rounded-xl border border-[#C8922A]/20 bg-[#C8922A]/5">
      <div className="flex items-center justify-between pl-3 pr-2 pt-2 pb-1">
        <span className="text-[9px] font-bold uppercase tracking-[0.16em] text-[#E6C878]/80">
          Suggested ({suggestions.length})
        </span>
        {overflowing && (
          <div className="flex items-center gap-1">
            <ScrollBtn label="◀" title="Scroll left" disabled={!canLeft} onClick={() => nudge(-1)} />
            <ScrollBtn label="▶" title="Scroll right" disabled={!canRight} onClick={() => nudge(1)} />
          </div>
        )}
      </div>
      <ul
        ref={scrollRef}
        onScroll={syncScroll}
        className="flex gap-2 overflow-x-auto px-2 pb-2 scrollbar-thin [scrollbar-color:rgba(200,146,42,0.35)_transparent]"
      >
        {suggestions.map((row) => {
          const product = row.product ?? {}
          const price = suggestionPrice(row)
          const removing = removingId === row.id
          return (
            <li
              key={row.id}
              className="relative shrink-0 w-28 rounded-xl border border-white/10 bg-[#14100D]/60 overflow-hidden"
            >
              <TileImage src={product.image || product.image_url} alt={product.name} />
              {row.staged && (
                <span className="absolute top-1 left-1 rounded-full bg-[#C8922A]/90 px-1.5 py-0.5 text-[7px] font-bold uppercase tracking-wider text-[#14100D]">
                  Pending
                </span>
              )}
              {!disabled && (
                <button
                  type="button"
                  disabled={removing}
                  onClick={() => onRemove(row.id)}
                  title="Remove this suggestion"
                  className="absolute top-1 right-1 w-5.5 h-5.5 inline-flex items-center justify-center rounded-full bg-black/60 backdrop-blur-sm border border-red-400/40 text-red-300 hover:bg-red-500/30 transition-colors text-[9px] disabled:opacity-40"
                >
                  {removing ? '…' : '✕'}
                </button>
              )}
              <div className="px-2 py-1.5">
                <p className="text-[10px] font-semibold text-white/85 leading-tight line-clamp-2 min-h-6.5">
                  {product.name || `Product #${row.product_id}`}
                </p>
                <p className="text-[9px] text-white/40 mt-1">
                  Qty {Number(row.quantity) || 1}
                  {price != null && <> · <span className="text-[#E6C878] font-semibold">{formatRate(price)}</span></>}
                </p>
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

/** Full-bleed square image atop a suggestion tile. */
function TileImage({ src, alt }) {
  return (
    <div className="aspect-square w-full bg-[#14100D] border-b border-white/5 flex items-center justify-center overflow-hidden">
      <SafeImg src={src} alt={alt} fallbackClass="text-xl" />
    </div>
  )
}

/**
 * Product image that degrades to the jewel placeholder when the URL is
 * missing OR fails to load (the admin server currently 403s /storage/*,
 * so every catalog image is dead until that deployment issue is fixed —
 * without this the tiles render ugly broken-image alt text).
 */
function SafeImg({ src, alt, fallbackClass = 'text-sm' }) {
  const [failed, setFailed] = useState(false)
  if (!src || failed) {
    return <span className={`opacity-40 ${fallbackClass}`} aria-hidden="true">💎</span>
  }
  return (
    <img
      src={src}
      alt={alt || 'Product'}
      loading="lazy"
      onError={() => setFailed(true)}
      className="w-full h-full object-cover"
    />
  )
}

function ScrollBtn({ label, title, disabled, onClick }) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className="w-6 h-6 inline-flex items-center justify-center rounded-full border border-[#C8922A]/30 text-[#E6C878] text-[9px] hover:bg-[#C8922A]/15 transition-colors disabled:opacity-25 disabled:cursor-not-allowed"
    >
      {label}
    </button>
  )
}

function ResultRow({ product, suggested, adding, onSuggest }) {
  const price = Number(product.price)
  const outOfStock = product.in_stock === false
  return (
    <li className="flex items-center gap-2.5 rounded-xl border border-white/10 bg-white/3 hover:border-[#C8922A]/35 transition-colors px-2.5 py-2">
      <Thumb src={product.image_url || product.image} alt={product.name} />
      <div className="min-w-0 grow">
        <p className="text-[11px] font-semibold text-white/85 leading-snug line-clamp-2">{product.name}</p>
        <p className="text-[10px] mt-0.5">
          <span className="text-[#E6C878] font-semibold">{Number.isFinite(price) ? formatRate(price) : '—'}</span>
          {product.grade && <span className="text-white/35"> · {product.grade}</span>}
          {outOfStock && <span className="text-red-300/90"> · Out of stock</span>}
        </p>
      </div>
      {suggested ? (
        <span className="shrink-0 text-[8px] font-bold uppercase tracking-wider text-green-300 bg-green-500/10 border border-green-400/25 rounded-full px-2 py-1">
          ✓ Added
        </span>
      ) : (
        <button
          type="button"
          disabled={adding || outOfStock}
          onClick={() => onSuggest(product, 1)}
          title={outOfStock ? 'This product is out of stock.' : 'Suggest to the customer'}
          className="shrink-0 rounded-full px-3 py-1.5 text-[8px] font-bold uppercase tracking-wider bg-linear-to-r from-[#C8922A] to-[#E5A820] text-[#14100D] hover:opacity-90 transition-opacity disabled:opacity-40"
        >
          {adding ? '…' : '+ Suggest'}
        </button>
      )}
    </li>
  )
}

function PagerBtn({ label, disabled, onClick }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="rounded-full border border-[#C8922A]/30 px-3 py-1 text-[9px] font-bold uppercase tracking-widest text-[#E6C878] hover:bg-[#C8922A]/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
    >
      {label}
    </button>
  )
}

function Thumb({ src, alt }) {
  return (
    <div className="w-10 h-10 rounded-lg border border-white/10 bg-[#14100D] flex items-center justify-center overflow-hidden shrink-0">
      <SafeImg src={src} alt={alt} />
    </div>
  )
}
